import moment from "moment";
import { useEffect, useState, useRef } from "react";
import { formatSecs, iAxios, STATUS_COLOR_MAP } from "../../lib/utils";
import { Spinner } from "../common/Helper";
import Icon from "../ds/Icon";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { alertMessage } from "../../lib/actions";
import classNames from "classnames";
import { ResponsiveContainer, PieChart, Pie, Cell, Legend, Tooltip } from "recharts";
import { DATA_COLORS } from "../../lib/chartTheme";

import { Annotorious } from "@recogito/annotorious";
import '@recogito/annotorious/dist/annotorious.min.css';

const MOD_TM = 3e5 / 2;

// Display labels for classifier sources returned by /python/classification-info,
// keyed by the "model" field the backend sends (see CLASSIFICATION_SOURCES in flask_app.py).
const MODEL_LABELS = { resnet: 'Resnet', vlm: 'VLM', ocr: 'OCR' };

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const name = payload[0].name;
        const fontSize = name.length > 25 ? '11px' : '12px';
        return (
            <div style={{
                background: 'var(--ds-surface)',
                padding: '8px 10px',
                borderRadius: '10px',
                boxShadow: '0 6px 24px rgba(26, 22, 60, 0.14)',
                maxWidth: '85%',
                pointerEvents: 'none',
                textAlign: 'left',
                border: '1px solid var(--ds-border-strong)',
                fontFamily: 'var(--ds-font-body)'
            }}>
                <p style={{
                    margin: 0,
                    fontWeight: '700',
                    color: 'var(--ds-ink)',
                    fontSize: fontSize,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: '1.4'
                }}>
                    {name}
                </p>
                <p style={{ margin: '4px 0 0 0', color: 'var(--ds-accent)', fontWeight: '600', fontSize: '11px' }}>
                    Count: {payload[0].value}
                </p>
            </div>
        );
    }
    return null;
};


// Styles for the image labeller UI
const styles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0,0,0,0.85)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
    },
    contentWrapper: {
        display: 'flex',
        width: '90%',
        height: '85%',
        backgroundColor: '#fff',
        borderRadius: '8px',
        overflow: 'hidden'
    },
    imageContainer: {
        flex: 3,
        padding: '20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#222',
        position: 'relative'
    },
    sidebar: {
        flex: 1,
        padding: '40px 20px 20px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        borderLeft: '1px solid #ddd',
        position: 'relative',
        overflowY: 'auto'
    },
    input: {
        width: '100%',
        padding: '10px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box'
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%'
    },
    saveBtn: {
        padding: '8px 20px',
        backgroundColor: '#2563eb',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        alignSelf: 'flex-end',
        marginTop: '12px',
        transition: 'all 0.2s ease',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
    },
    closeBtnX: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        background: 'none',
        border: 'none',
        fontSize: '20px',
        cursor: 'pointer',
        color: '#999',
        padding: '5px 10px',
        borderRadius: '50%',
        transition: 'background 0.2s',
    },
    navBtn: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.25)',
        background: 'rgba(0,0,0,0.55)',
        color: '#fff',
        fontSize: '22px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        transition: 'background 0.2s',
        zIndex: 1,
    },
    labelPill: {
        padding: '4px 12px',
        borderRadius: '16px',
        border: 'none',
        color: 'white',
        fontSize: '12px',
        cursor: 'pointer',
        fontWeight: '500',
        transition: 'opacity 0.2s',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }
};

const UserActivity = () => {
    const dispatch = useDispatch()
    let params = useParams();
    let { username } = params;
    const [data, setData] = useState(null);
    // Ordered list of { model, available, data, screenshots } from /python/classification-info,
    // one entry per classifier source (resnet, vlm) — array order is display order.
    const [classificationResults, setClassificationResults] = useState([]);
    // Per-model lookup: { resnet: { filename: {...} }, vlm: { filename: {...} } }
    const [screenshotsByModel, setScreenshotsByModel] = useState({});
    const [engagement, setEngagement] = useState([]);
    const [logs, setLogs] = useState([]);
    const [maxPage, setMaxPage] = useState(36);
    const [version, setVersion] = useState(Date.now())
    const userInfo = useSelector(st => st.userInfo);
    const [error, setError] = useState(null);
    const [deletingUrl, setDeletingUrl] = useState(null);
    const [hoveredUrl, setHoveredUrl] = useState(null);
    const [armedUrl, setArmedUrl] = useState(null);



    // ******************  IMAGE LABELLER LOGIC *************************

    /* 
    Image labeller feature:
    What it does/ can do:
    1. When we click an image for labelling, the image is opened in an overlay, like Google Photos.
    2. In this overlay, the whole image is selected and on the right side, there will be a sidebar where we can label in the input box.
    3. We can drag from this whole image selection to where we want and then label.
    4. On clicking outside our selection, the selection is lost.
    5. After this, we can drag and select as well.
    6. Whole image selection is only for 1 labelling. After that, that selection is gone. If you need it again, you have to press in the right-top  "X" and then select the same image again.
    7. We can use "Esc" key to cancel selection and press "Enter" for submitting.
    8. The label and the selection is send to backend. Backend can store this data locally or in DO space. This is configured inside `server_py/`

    NOTE: On labelling, the image closes automatically.
    */

    // State variables
    const [isViewerOpen, setIsViewerOpen] = useState(false);
    const [activeImage, setActiveImage] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(null);
    const [selectedAnno, setSelectedAnno] = useState(null);
    const [labelText, setLabelText] = useState("");
    const [existingLabel, setExistingLabel] = useState({ labels: [], data: {} });

    // Label Renaming State
    const [editingLabel, setEditingLabel] = useState(null);
    const [newLabelName, setNewLabelName] = useState("");

    // Reference variables from DOM
    const imgRef = useRef(null);
    const annoRef = useRef(null);
    const selectedAnnoRef = useRef(null);
    const inputRef = useRef(null);

    // Variables for showing existing labels
    const CACHE_KEY = "label_data_cache";
    const TIMESTAMP_KEY = "local_cache_timestamp";
    const FIVE_MINUTES = 5 * 60 * 1000;

    // **** HELPER FUNCTIONS ****

    // Function to trigger the initial full-image selection
    // This ensures that whole image is selected on loading the image.
    const handleImageLoad = () => {
        if (!annoRef.current || !imgRef.current) return;

        const { naturalWidth, naturalHeight } = imgRef.current;
        const fullImageId = '#full-image-init';

        const fullImageAnno = {
            type: "Annotation",
            id: fullImageId,
            body: [],
            target: {
                selector: {
                    type: "FragmentSelector",
                    conformsTo: "http://www.w3.org/TR/media-frags/",
                    value: `xywh=pixel:0,0,${naturalWidth},${naturalHeight}`
                }
            }
        };

        annoRef.current.addAnnotation(fullImageAnno);
        annoRef.current.selectAnnotation(fullImageId);

        // Sync with our React state and Ref immediately
        setSelectedAnno(fullImageAnno);
        selectedAnnoRef.current = fullImageAnno;

        // Use stored label
        setLabelText(getInitialLabel());
    };

    // Get the initial label from Local Storage
    const getInitialLabel = () => {
        return localStorage.getItem('lastSavedLabel') || "";
    };

    // Function to handle sending data to backend
    const handleFinalSave = async (passedLabel = null) => {
        const currentAnno = selectedAnnoRef.current;

        const finalLabel = passedLabel || labelText;

        if (!currentAnno || !finalLabel) {
            alert("Please select an area and enter a label.");
            return;
        }

        // Ensure ID exists
        const finalId = currentAnno.id || `#custom-${Date.now()}`;

        // Getting the selector from our selection
        const selector = currentAnno.target?.selector;
        const value = Array.isArray(selector) ? selector[0].value : selector?.value;
        if (!value) return;

        // Getting coordinates from selector
        // Coordinates format:  xywh=pixel:value1,value2,value3,value4
        const coordString = value.includes(':') ? value.split(':')[1] : value;
        const [x, y, w, h] = coordString.split(',').map(Number);

        // Data to send to the backend
        const payload = {
            "image_URL": activeImage,
            "selection_coordinates": value,
            "normalized_selection_coordinates": {
                "x_norm": x / imgRef.current.naturalWidth,
                "y_norm": y / imgRef.current.naturalHeight,
                "w_norm": w / imgRef.current.naturalWidth,
                "h_norm": h / imgRef.current.naturalHeight
            },
            "label": finalLabel,
            "timestamp": Date.now(),
            "annotation_ID": finalId
        };

        try {
            await iAxios.post('/python/labelled-image', payload);

            // Append new label to localStorage cache 
            const cached = JSON.parse(localStorage.getItem(CACHE_KEY));
            if (cached && !cached.labels.includes(finalLabel)) {
                cached.labels.push(finalLabel);
                // Assign a default color if it's brand new
                if (!cached.data[finalLabel]) cached.data[finalLabel] = "#6c757d";
                localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
                setExistingLabel(cached); // Update state so UI reflects it immediately
            }

            // Save the label to localStorage
            localStorage.setItem('lastSavedLabel', finalLabel);     // Can REFACTOR

            // Removing the selection
            // Remove the box FIRST, then cancel the selection state
            if (finalId) annoRef.current.removeAnnotation(finalId);
            annoRef.current.cancelSelected();

            selectedAnnoRef.current = null;
            setSelectedAnno(null);
            setLabelText("");

            // Close the viewer
            closeViewer();

        } catch (err) {
            console.error("Upload failed:", err);
            alert("Failed to save label. Please try again.")
        }
    };

    // Function to handle renaming a label (Admin only)
    const handleRenameSubmit = async (oldName) => {
        if (!newLabelName || newLabelName.trim() === "" || newLabelName === oldName) {
            setEditingLabel(null);
            return;
        }

        try {
            const response = await iAxios.put('/python/labels/rename', {
                old_name: oldName,
                new_name: newLabelName.trim()
            });

            if (response.data.success) {
                // Update local cache
                const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || '{"labels":[], "data":{}}');

                const newName = response.data.new_name;
                const oldIndex = cached.labels.indexOf(oldName);

                if (oldIndex > -1) {
                    if (cached.labels.includes(newName)) {
                        // The label already exists, so we just remove the old one (merge)
                        cached.labels.splice(oldIndex, 1);
                        delete cached.data[oldName];
                    } else {
                        // Safe rename mapping
                        cached.labels[oldIndex] = newName;
                        if (cached.data[oldName]) {
                            cached.data[newName] = cached.data[oldName];
                            delete cached.data[oldName];
                        }
                    }
                }

                localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
                setExistingLabel(cached);
                setEditingLabel(null);
                setNewLabelName("");
            }
        } catch (err) {
            console.error("Rename failed:", err);
            alert(err.response?.data?.error || "Failed to rename label. Please try again.");
        }
    };

    // Function to handle what happens on clicking the image from the dashboard. 
    // This triggers the process where we show the image as an overlay.
    function handleImageClick(event, imageUrl, idx) {
        event.preventDefault();
        setActiveImage(imageUrl);
        setCurrentIndex(idx);
        setIsViewerOpen(true);
    }

    // Function to handle closing of the image viewer
    function closeViewer() {
        setActiveImage(null);
        setIsViewerOpen(false);
        setSelectedAnno(null);
        setCurrentIndex(null);
    }

    // Helper function to fetch existing labels
    const fetchLabels = async () => {
        try {
            const response = await iAxios.get('/python/labels');
            const result = response.data;

            if (result.success) {
                localStorage.setItem(CACHE_KEY, JSON.stringify(result));
                localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());
                setExistingLabel(result);
            }
        } catch (err) {
            console.error("Failed to sync labels:", err)
        }
    }

    const handleQuickSubmit = (labelValue) => {
        // Submit existing or new label
        handleFinalSave(labelValue);

        // If it's a new label, update LocalStorage
        if (!existingLabel.labels.includes(labelValue)) {
            const updated = {
                ...existingLabel,
                labels: [...existingLabel.labels, labelValue],
                data: { ...existingLabel.data, [labelValue]: "#e0e0e0" }
            };
            localStorage.setItem(CACHE_KEY, JSON.stringify(updated));
            setExistingLabel(updated);
        }

        // Close the viewer
        closeViewer();
    }


    // **** HOOKS ****

    // This will ensure the focus is on the labelling input box.
    // This means we can type on keyboard directly to label after selecting without 
    // the need to click onto the label input. The previous input is stored and selected
    // automatically.
    useEffect(() => {
        if (selectedAnno && inputRef.current) {
            // Small timeout ensures the DOM element is rendered before focusing
            const timeout = setTimeout(() => {
                inputRef.current.focus();

                // This highlights all text in the input
                inputRef.current.select();
            }, 50);
            return () => clearTimeout(timeout);
        }
    }, [selectedAnno]);

    // This section handles the labelling
    useEffect(() => {
        if (!isViewerOpen || !imgRef.current) return;

        // Initialize and assign to Ref IMMEDIATELY
        const annotorious = new Annotorious({
            image: imgRef.current,
            disableEditor: true,
        });
        annoRef.current = annotorious;

        // Annotorious Version 2 specific listeners
        // 'changeSelectionTarget' fires while you are dragging handles
        annotorious.on('changeSelectionTarget', (target) => {
            // In v2, the event gives us the 'target', we reconstruct the annotation object
            const updatedAnno = {
                ...selectedAnnoRef.current,
                target: target
            };

            selectedAnnoRef.current = updatedAnno;
            setSelectedAnno(JSON.parse(JSON.stringify(updatedAnno)));
        });

        // Fires when you finish a new box or select an existing one
        annotorious.on('createSelection', (selection) => {
            const selectionwithId = {
                ...selection,
                id: `#custom-${Date.now()}` // Generating unique id
            };
            selectedAnnoRef.current = selectionwithId;
            setSelectedAnno(JSON.parse(JSON.stringify(selectionwithId)));

            // Autofill on new new box draw
            setLabelText(getInitialLabel());
        });

        // Fires when selection is done
        annotorious.on('selectAnnotation', (annotation) => {
            selectedAnnoRef.current = annotation;
            setSelectedAnno(JSON.parse(JSON.stringify(annotation)));

            // Only autofill if the annotation doesn't already have a body/label
            const existingLabel = annotation.body?.[0]?.value;
            setLabelText(existingLabel || getInitialLabel());
        });

        // Trigger selection if image is already loaded from cache
        if (imgRef.current.complete) {
            handleImageLoad();
        }

        // Cleanup
        return () => {
            annotorious.destroy();
            annoRef.current = null;
        };
    }, [isViewerOpen, activeImage]);

    // // Hook for rendering existing labels from local storage or after querying from backend
    useEffect(() => {
        if (isViewerOpen) {
            const cached = localStorage.getItem(CACHE_KEY);
            const lastFetch = localStorage.getItem(TIMESTAMP_KEY);
            const now = Date.now();

            if (cached && lastFetch && (now - parseInt(lastFetch) < FIVE_MINUTES)) {
                setExistingLabel(JSON.parse(cached));
            } else {
                fetchLabels();
            }
        }
    }, [isViewerOpen]);


    // This will help us to deselect when pressing "ESC" key.
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                if (selectedAnno) {
                    // If a box is selected, just cancel the selection
                    annoRef.current.cancelSelected();
                    setSelectedAnno(null);
                    selectedAnnoRef.current = null;
                    setLabelText("");
                } else if (isViewerOpen) {
                    // If no box is selected but viewer is open, close the whole thing
                    closeViewer();
                }
            } else if (isViewerOpen && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
                // Ignore arrow keys while the user is typing a label - let the
                // browser move the text cursor instead of navigating images.
                const active = document.activeElement;
                const isTyping = active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA');
                if (isTyping || currentIndex == null) return;

                const flatShots = buildSessions().flatMap(g => g.shots);
                const nextIndex = currentIndex + (event.key === 'ArrowLeft' ? -1 : 1);
                if (nextIndex < 0 || nextIndex >= flatShots.length) return;

                event.preventDefault();
                setActiveImage(flatShots[nextIndex].full);
                setCurrentIndex(nextIndex);
            }
        };

        // Listener for 'keydown' events with callack fn: `handleKeyDown`
        window.addEventListener('keydown', handleKeyDown);

        // Cleanup
        return () => window.removeEventListener('keydown', handleKeyDown);

    }, [isViewerOpen, selectedAnno, currentIndex, data, maxPage, screenshotsByModel]); // Dependencies ensure it knows current state

    // ******************  IMAGE LABELLER LOGIC ENDS *************************


    const [sp, setSearchParams] = useSearchParams();
    if (!username) {
        username = userInfo.username;
    }

    useEffect(() => {
        setData(null);
        let date = sp.get('date');
        if (!date) {
            setSearchParams((prev) => {
                prev.set('date', moment().format('YYYY-MM-DD'));
                return prev;
            }, { replace: true })
            return
        }

        let handle = null;
        iAxios.get('/api/v1/track/user-activity', {
            params: {
                offset: new Date().getTimezoneOffset(),
                date,
                username
            }
        }).then(resp => {
            if (!resp.data.success) {
                setData([]);
                setEngagement([])
                setError(resp.data.message)
                return;
            }

            const user = resp.data.user;
            const targetEmail = resp.data.targetEmail || `${username}@ceegees.in`;

            // CLASSIFICATION INFO BOX
            const fetchInfo = () => {
                iAxios.get('/python/classification-info', {
                    params: {
                        date,
                        email: targetEmail
                    }
                }).then(classificationResp => {
                    if (!classificationResp.data.success) {
                        setClassificationResults([]);
                        setScreenshotsByModel({});
                        // Let main error handling take precedence if needed, but don't overwrite
                        return;
                    }
                    const results = Array.isArray(classificationResp.data.results) ? classificationResp.data.results : [];
                    setClassificationResults(results);

                    const byModel = {};
                    results.forEach(r => {
                        const mapping = {};
                        (r.screenshots || []).forEach(s => {
                            if (s.filename) {
                                mapping[s.filename] = s;
                            }
                        });
                        byModel[r.model] = mapping;
                    });
                    setScreenshotsByModel(byModel);
                }).catch(err => {
                    console.error('Classification error:', err.message);
                    setClassificationResults([]);
                    setScreenshotsByModel({});
                });
            };

            // Initial load
            fetchInfo();
            // Re-poll every hour
            handle = setInterval(fetchInfo, 60 * 60 * 1000); // 60 minutes


            const { data: list, logs } = resp.data;
            setLogs(logs)
            if (list.length === 0) {
                setData([])
                setEngagement([])
                return;
            }
            list.sort((a, b) => b.tm - a.tm);
            setData(list);
            let last = list.length - 1;
            const startMin = Math.floor(list[last].tm / MOD_TM);
            const eng = [];
            const max1 = startMin + Math.floor((36e5 * 9.5) / MOD_TM);
            const max = Math.floor(Date.now() / MOD_TM);
            for (let min = startMin; min <= max1;) {
                const m1 = Math.floor(list[last].tm / (MOD_TM));
                let dec = false;
                const tmStr = moment(min * MOD_TM).format('hh:mm a');
                if (m1 < min) {
                    dec = true;
                } if (m1 > min) {
                    dec = false;
                    eng.push({
                        min,
                        val: 0,
                        tmStr,
                        cls: 'ds-cell-dark'
                    });
                } else if (m1 === min) {
                    eng.push({
                        min,
                        val: 1,
                        tmStr,
                        url: list[last].url,
                        cls: 'ds-cell-good'
                    });
                    dec = true;
                    min++;
                }

                if (dec && last > 0) {
                    last--;
                } else {
                    min++;
                    eng.push({
                        min,
                        tmStr,
                        cls: min < max ? 'ds-cell-dark' : 'ds-cell-idle',
                        val: 0
                    });
                }
            }
            setEngagement(eng);
        })

        return () => {
            if (handle) clearInterval(handle);
        };
    }, [version, sp, username]);

    const increaseMax = () => {
        setMaxPage(maxPage + 60);
    }

    const handleDeleteScreenshot = async (item) => {
        setArmedUrl(null);
        setDeletingUrl(item.url);
        try {
            const resp = await iAxios.delete('/api/v1/admin/screenshot', { data: { imageUrl: item.url } });
            if (resp.data.success) {
                setData(prev => prev.filter(s => s.url !== item.url));
            } else {
                dispatch(alertMessage(resp.data.message || 'Failed to delete screenshot', 'Error'));
            }
        } catch (err) {
            console.error('Delete screenshot failed', err);
            dispatch(alertMessage('Failed to delete screenshot', 'Error'));
        } finally {
            setDeletingUrl(null);
        }
    };

    const reCompute = () => {

        setData(null);
        iAxios.get('/api/v1/track/re-compute', {
            params: {
                offset: new Date().getTimezoneOffset(),
                date,
                username
            }
        }).then(resp => {
            console.log(resp);
            setVersion(Date.now())

        })
    }
    const analyzeDay = () => {
        iAxios.get('/api/v1/track/analyse-day', {
            params: {
                offset: new Date().getTimezoneOffset(),
                date,
                username
            }
        }).then(resp => {
            console.log(resp);
        })
    }

    let date = sp.get('date');

    // Day summary computed from punch logs + screenshots
    const dayStats = (Array.isArray(logs) && logs.length > 0) ? logs.reduce((a, l) => {
        const at = parseInt(`${l.action_time}`);
        const dur = parseInt(`${l.action_duration}`) || 0;
        if (l.action_group === 'In') { a.inMs += dur; a.sessions++; if (a.firstIn == null || at < a.firstIn) a.firstIn = at; }
        if (l.action_group === 'Break') a.breakMs += dur;
        if (at + dur > a.lastOut) a.lastOut = at + dur;
        a.screensMin += parseInt(`${l.screens}`) || 0;
        return a;
    }, { inMs: 0, breakMs: 0, sessions: 0, firstIn: null, lastOut: 0, screensMin: 0 }) : null;

    // Renders one screenshot tile (reused inside activity sessions)
    const renderShot = (item, idx) => {
        const fileUrl = item.full || item.url || '';
        const filenameMatch = fileUrl.split('/').pop().split('?')[0];
        // One label line per model that actually classified this screenshot —
        // a model with no entry for this file (e.g. mid-backfill, or not run yet) is skipped, not blanked.
        const modelLabels = classificationResults
            .filter(r => r.available)
            .map(r => ({ model: r.model, classification: screenshotsByModel[r.model]?.[filenameMatch] }))
            .filter(({ classification }) => classification);
        return (
            <div key={item.tm} className="ua-shot" onMouseLeave={() => setArmedUrl(null)}>
                <a href={item.full} rel="noreferrer" target="_blank" onClick={(e) => handleImageClick(e, item.full, idx)}>
                    <img src={item.url} alt="screenshot" onError={(e) => e.target.src = '/missing.png'} />
                </a>
                {modelLabels.length > 0 && (
                    <span className="ua-shot__label">
                        {modelLabels.map(({ model, classification }) => (
                            <span key={model} className="ua-shot__label-line">
                                {`${MODEL_LABELS[model] || model}: ${classification.application}: ${(classification.confidence * 100).toFixed(0)}%`}
                            </span>
                        ))}
                    </span>
                )}
                {userInfo.role === 'Admin' && <span className="ua-shot__dot"
                    style={{ background: item.diff <= 1000 ? 'var(--ds-danger)' : 'var(--ds-success)' }} />}
                {userInfo.role === 'Admin' && (
                    armedUrl === item.url ? (
                        <div className="activity-del" style={{ opacity: 1, bottom: '36px' }}>
                            <button className="activity-del__btn is-confirm" onClick={() => handleDeleteScreenshot(item)} title="Confirm delete" aria-label="Confirm delete"><Icon name="check" /></button>
                            <button className="activity-del__btn" onClick={() => setArmedUrl(null)} title="Cancel" aria-label="Cancel delete"><Icon name="close" /></button>
                        </div>
                    ) : (
                        <div className="activity-del" style={{ bottom: '36px' }}>
                            <button className="activity-del__btn is-danger" onClick={() => setArmedUrl(item.url)} disabled={deletingUrl === item.url} title="Delete screenshot" aria-label="Delete screenshot">
                                {deletingUrl === item.url ? '…' : <Icon name="trash" />}
                            </button>
                        </div>
                    )
                )}
                <div className="ua-shot__time">{moment(item.tm).format('hh:mm a')}</div>
            </div>
        );
    };

    // Group the visible screenshots into fixed 30-minute interval blocks
    // (e.g. 9:00–9:30, 9:30–10:00). Predictable time windows, with the app(s)
    // seen in each block shown as secondary context.
    const buildSessions = () => {
        const visible = (data || []).filter((_, idx) => idx < maxPage);
        const BLOCK = 30 * 60 * 1000;
        const buckets = new Map();
        visible.forEach(item => {
            const start = Math.floor(item.tm / BLOCK) * BLOCK;
            if (!buckets.has(start)) {
                buckets.set(start, []);
            }
            buckets.get(start).push(item);
        });
        // Session chip app names use only the primary model (resnet if available, else vlm) —
        // showing both models' labels here would double the distinct-app count per session.
        const primaryModel = classificationResults.find(r => r.available)?.model;
        const primaryScreenshots = primaryModel ? screenshotsByModel[primaryModel] : null;
        return [...buckets.entries()]
            .sort((a, b) => b[0] - a[0]) // newest block first
            .map(([start, shots]) => {
                const apps = [...new Set(shots.map(s => {
                    const fn = (s.full || s.url || '').split('/').pop().split('?')[0];
                    return primaryScreenshots?.[fn]?.application;
                }).filter(Boolean))];
                return { from: start, to: start + BLOCK, shots, apps };
            });
    };

    // Sessions/flat shot order for the current render, shared between the
    // grid (to assign each shot its viewer-navigation index) and the
    // prev/next controls in the image viewer overlay.
    const sessions = data ? buildSessions() : [];
    const flatShots = sessions.flatMap(g => g.shots);
    const shotIndexMap = new Map(flatShots.map((s, i) => [s, i]));

    // Move the open viewer to the shot at `nextIndex`, if it exists.
    const goToIndex = (nextIndex) => {
        if (nextIndex < 0 || nextIndex >= flatShots.length) return;
        setActiveImage(flatShots[nextIndex].full);
        setCurrentIndex(nextIndex);
    };

    // One "Productivity" pie-chart panel per classifier source. Rendered in
    // classificationResults array order (resnet first, then vlm) — a source with
    // no data simply isn't in the array, so no panel/gap is rendered for it.
    const renderClassificationPanel = (result) => {
        const classificationData = result.data;
        const COLORS = DATA_COLORS;
        const chartData = Object.keys(classificationData)
            .filter(key => key !== 'summary')
            .map((key, index) => {
                const name = key.charAt(0).toUpperCase() + key.slice(1);
                const value = Math.round(parseFloat(classificationData[key]) || 0);
                const color = existingLabel?.data?.[key] ||
                    existingLabel?.data?.[key.toLowerCase()] ||
                    COLORS[index % COLORS.length];
                return { name, value, color };
            });

        return (
            <div className="ds-panel" style={{ padding: 'var(--ds-space-4)', textAlign: 'left' }} key={result.model}>
                <h4 style={{ marginBottom: 'var(--ds-space-2)' }}>
                    Productivity{' '}
                    <span style={{ fontWeight: 400, color: 'var(--ds-ink-muted)', fontSize: 'var(--ds-text-xs)' }}>
                        ({MODEL_LABELS[result.model] || result.model})
                    </span>
                </h4>
                <div style={{ width: '100%' }}>
                    <ResponsiveContainer width="100%" aspect={1}>
                        <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                            <Pie data={chartData} cx="50%" cy="50%" innerRadius="50%" outerRadius="90%" paddingAngle={3} dataKey="value" stroke="var(--ds-surface)">
                                {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                    <div style={{ marginTop: 'var(--ds-space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-2)' }}>
                        {chartData.map((item) => (
                            <div key={item.name} style={{ display: 'flex', gap: 'var(--ds-space-2)', alignItems: 'flex-start' }}>
                                <span style={{ minWidth: '8px', height: '8px', borderRadius: '50%', backgroundColor: item.color, marginTop: '4px' }} />
                                <span style={{ fontSize: 'var(--ds-text-xs)', lineHeight: '1.4', color: 'var(--ds-ink-soft)' }}>{item.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <hr style={{ margin: 'var(--ds-space-4) 0', border: 0, borderTop: '1px solid var(--ds-border)' }} />
                <div style={{ fontSize: 'var(--ds-text-sm)', color: 'var(--ds-ink-muted)' }}>
                    <strong>Summary:</strong> {classificationData.summary || 'No additional info'}
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="page-head" style={{ justifyContent: 'space-between' }}>
                <h2>{username}</h2>
                <input type="date" className="ds-input"
                    max={moment().format('YYYY-MM-DD')} style={{ maxWidth: '200px' }}
                    defaultValue={moment(date).format('YYYY-MM-DD')} onChange={e =>
                        setSearchParams((prev) => {
                            prev.set('date', moment(e.target.value).format('YYYY-MM-DD'));
                            return prev;
                        })} />
            </div>
            <div>
                {!data && <Spinner />}
                {dayStats && <div className="ua-summary">
                    <div className="ua-stat"><div className="ua-stat__v">{dayStats.inMs ? formatSecs(Math.round(dayStats.inMs / 1000)) : '—'}</div><div className="ua-stat__l">Worked</div></div>
                    <div className="ua-stat"><div className="ua-stat__v">{dayStats.breakMs ? formatSecs(Math.round(dayStats.breakMs / 1000)) : '—'}</div><div className="ua-stat__l">Break</div></div>
                    <div className="ua-stat"><div className="ua-stat__v">{dayStats.screensMin ? formatSecs(dayStats.screensMin * 60) : '—'}</div><div className="ua-stat__l">Tracked</div></div>
                    <div className="ua-stat"><div className="ua-stat__v">{dayStats.sessions}</div><div className="ua-stat__l">Sessions</div></div>
                    <div className="ua-stat"><div className="ua-stat__v">{dayStats.firstIn ? moment(dayStats.firstIn).format('hh:mm a') : '—'}</div><div className="ua-stat__l">First in</div></div>
                    <div className="ua-stat"><div className="ua-stat__v">{dayStats.lastOut ? moment(dayStats.lastOut).format('hh:mm a') : '—'}</div><div className="ua-stat__l">Last out</div></div>
                </div>}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--ds-space-5)', alignItems: 'flex-start' }}>
                    {data?.length > 0 && <div className='ua-timeline' style={{ flexBasis: '100%', marginBottom: 'var(--ds-space-2)' }}>
                        <div className="ua-timeline__strip">
                            {engagement.map((item, idx) => (
                                <div
                                    key={`idx-${idx}`}
                                    title={`${item.tmStr}${item.url ? '' : ' · no activity'}`}
                                    className={`activity-cell ${item.cls}`}>
                                    {item.url && <div className="ua-tl__pop">
                                        <img src={item.url} alt={item.tmStr} />
                                        <span className="ua-tl__pop-time">{item.tmStr}</span>
                                    </div>}
                                </div>
                            ))}
                        </div>
                    </div>}

                    <div style={{ flex: '1 1 360px', minWidth: 0 }}>
                        {error && <h3 style={{ color: 'var(--ds-danger)' }}>{error}</h3>}
                        {data && data.length === 0 && error == null && <div className="issue-empty">
                            No screens recorded for {moment(date).format('DD MMM YYYY')}
                        </div>}

                        {data && sessions.map((g, gi) => (
                            <div className="ua-session" key={`sess-${gi}`}>
                                <div className="ua-session__head">
                                    <span className="ua-session__app">{moment(g.from).format('h:mm')} – {moment(g.to).format('h:mm a')}</span>
                                    <span className="ua-session__meta">
                                        {g.apps.length > 0 && `${g.apps.slice(0, 3).join(', ')}${g.apps.length > 3 ? ` +${g.apps.length - 3}` : ''} · `}
                                        {g.shots.length} {g.shots.length === 1 ? 'shot' : 'shots'}
                                    </span>
                                </div>
                                <div className="ua-grid">{g.shots.map(item => renderShot(item, shotIndexMap.get(item)))}</div>
                            </div>
                        ))}


                        {maxPage < data?.length && <div style={{ textAlign: 'center', marginTop: 'var(--ds-space-4)' }}>
                            <button className="ds-btn is-soft" onClick={increaseMax}>
                                Showing {maxPage}/{data.length} — show more</button>
                        </div>}
                    </div>

                    <div style={{ flex: '0 0 260px', display: 'flex', flexDirection: 'column', gap: 'var(--ds-space-4)' }}>
                        {(data && userInfo.role === 'Admin') && <div className="ds-field-row" style={{ marginBottom: 0 }}>
                            <button onClick={analyzeDay} className="ds-btn is-primary is-sm">Analyse my day</button>
                            <button className="ds-btn is-ghost is-sm" onClick={reCompute}>Re-compute</button>
                        </div>}

                        {/* Classification Pie charts — one per available model (resnet, then vlm) */}
                        {classificationResults.filter(r => r.available).map(renderClassificationPanel)}

                        {logs.length > 0 && <div className="ua-log-title">Sessions</div>}
                        {logs.map(item => {
                            const color = STATUS_COLOR_MAP[item.action_group];
                            const at = parseInt(`${item.action_time}`);
                            const dur = parseInt(`${item.action_duration}`);
                            return <div key={item.id} className={`ua-log ${color}`} title={item.action}>
                                <div className="ua-log__top">
                                    <span className="ua-log__type">{item.action_group}</span>
                                    <span className="ua-log__dur">{formatSecs(dur / 1000)}</span>
                                </div>
                                <div className="ua-log__time">{moment(at).format('hh:mm a')} – {moment(at + dur).format('hh:mm a')}</div>
                                {item.screens > 0 && <div className="ua-log__meta">Tracked {formatSecs(item.screens * 60)}</div>}
                            </div>;
                        })}
                    </div>
                </div>

                {/* Image labeller UI */}
                {isViewerOpen && (
                    <div className="custom-overlay" style={styles.overlay}>
                        <div className="main-content" style={styles.contentWrapper}>

                            {/* LEFT: Image Container */}
                            <div className="image-container" style={styles.imageContainer}>
                                <img
                                    ref={imgRef}
                                    src={activeImage}
                                    alt="Screenshot for labelling"
                                    onLoad={handleImageLoad}
                                    style={{ maxWidth: '100%', maxHeight: '80vh' }}
                                />
                                {currentIndex > 0 && (
                                    <button
                                        onClick={() => goToIndex(currentIndex - 1)}
                                        style={{ ...styles.navBtn, left: '20px' }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.75)'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.55)'}
                                        title="Previous (←)"
                                        aria-label="Previous screenshot"
                                    >
                                        ‹
                                    </button>
                                )}
                                {currentIndex != null && currentIndex < flatShots.length - 1 && (
                                    <button
                                        onClick={() => goToIndex(currentIndex + 1)}
                                        style={{ ...styles.navBtn, right: '20px' }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.75)'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(0,0,0,0.55)'}
                                        title="Next (→)"
                                        aria-label="Next screenshot"
                                    >
                                        ›
                                    </button>
                                )}
                            </div>

                            {/* RIGHT: External Sidebar Editor */}
                            <div className="sidebar-editor" style={styles.sidebar}>
                                {/* Modern X Close Button */}
                                <button
                                    onClick={closeViewer}
                                    style={styles.closeBtnX}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                    title="Close (Esc)"
                                >
                                    ✕
                                </button>
                                <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}>Label Image</h3>
                                {selectedAnno ? (
                                    <div>
                                        <p style={{ fontSize: '12px', color: '#666' }}>Selection Active</p>
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={labelText}
                                            onChange={(e) => setLabelText(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleFinalSave()}
                                            placeholder="Enter label..."
                                            style={styles.input}
                                            autoFocus
                                        />

                                        {/* Clickable Labels */}
                                        <div style={{ marginTop: '15px', display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: '220px', overflowY: 'auto', paddingRight: '4px' }}>
                                            {[...(existingLabel.labels ?? [])].sort((a, b) => a.localeCompare(b)).map((label) => (
                                                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    {editingLabel === label ? (
                                                        <input
                                                            type="text"
                                                            value={newLabelName}
                                                            onChange={(e) => setNewLabelName(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') handleRenameSubmit(label);
                                                                if (e.key === 'Escape') setEditingLabel(null);
                                                            }}
                                                            onBlur={() => handleRenameSubmit(label)}
                                                            autoFocus
                                                            style={{
                                                                padding: '4px 8px',
                                                                borderRadius: '16px',
                                                                border: '1px solid #007bff',
                                                                fontSize: '12px',
                                                                outline: 'none',
                                                                width: '100px'
                                                            }}
                                                        />
                                                    ) : (
                                                        <div style={{
                                                            ...styles.labelPill,
                                                            backgroundColor: existingLabel.data[label] || '#ccc',
                                                            display: 'flex',
                                                            alignItems: 'stretch',
                                                            padding: '0',
                                                            overflow: 'hidden'
                                                        }}>
                                                            <button
                                                                onClick={() => handleFinalSave(label)}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    color: 'white',
                                                                    cursor: 'pointer',
                                                                    padding: '4px 10px',
                                                                    fontSize: '12px',
                                                                    fontWeight: '500'
                                                                }}
                                                            >
                                                                {label}
                                                            </button>
                                                            {userInfo.role === 'Admin' && (
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingLabel(label);
                                                                        setNewLabelName(label);
                                                                    }}
                                                                    title="Rename label"
                                                                    style={{
                                                                        background: 'rgba(0,0,0,0.1)',
                                                                        border: 'none',
                                                                        borderLeft: '1px solid rgba(255,255,255,0.2)',
                                                                        cursor: 'pointer',
                                                                        padding: '4px 8px',
                                                                        color: 'white',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        transition: 'background 0.2s',
                                                                    }}
                                                                    onMouseEnter={(e) => e.target.style.background = 'rgba(0,0,0,0.2)'}
                                                                    onMouseLeave={(e) => e.target.style.background = 'rgba(0,0,0,0.1)'}
                                                                >
                                                                    <span style={{ fontSize: '10px' }}>✏️</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        {/* Button container */}
                                        <div style={styles.buttonContainer}>
                                            <button
                                                onClick={() => handleFinalSave()}
                                                style={styles.saveBtn}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
                                            >
                                                Submit Label
                                            </button>
                                        </div>

                                    </div>
                                ) : (
                                    <p>Draw a box on the image or use the initial selection to label.</p>
                                )}
                            </div>

                        </div>
                    </div>
                )}
            </div>
        </div>

    );
};


export default UserActivity;